import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  getPreviousPlay,
  getIsEndOfGame,
  transformGameStatusIfDateAdded
} from "games/utils";
import { transformPlays, transformPlay } from "./transformations";
import { prePlayChanges } from "./prePlay";
import { postStatsChanges } from "./postStats";
import { getPointsDiff } from "./postPoints";
import {
  calculatePlays,
  setNullTeamStats,
  setNullPlayerStats
} from "./actions";

const getGameStatus = (play, gameStatus) => {
  const { playType, quarter } = play;

  if (playType === "endOfQuarter" && quarter === 2) {
    return "HALFTIME";
  } else if (playType === "weatherDelay") {
    return "WEATHER_DELAY";
  } else if (getIsEndOfGame(play) || gameStatus === "FINAL") {
    return "FINAL";
  } else {
    return "LIVE";
  }
};

const setState = (state, action) => {
  const newState = action.payload;
  return {
    ...state,
    ...newState
  };
};

const addNewPlayBase = (state, play) => {
  const previousPlay = getPreviousPlay(state.plays);
  const transformedPlay = transformPlay(play, previousPlay, state.game);
  const transformedPlays = [
    ...state.plays,
    {
      id: uuidv4(),
      ...transformedPlay
    }
  ];
  const transformedStats = postStatsChanges({
    plays: transformedPlays,
    state
  });

  const gameStatus = getGameStatus(transformedPlay, state.gameStatus);

  return {
    ...state,
    plays: transformedPlays,
    shouldSave: true,
    stats: transformedStats,
    gameStatus: gameStatus
  };
};

const addNewPlay = (state, action) => {
  const play = action.payload;
  return addNewPlayBase(state, play);
};

const endQuarter = (state, action) => {
  const lastPlay = _.get(state.plays, state.plays.length - 1);
  const endOfQuarterPlay = {
    playType: "endOfQuarter"
  };
  const play = {
    ...endOfQuarterPlay,
    ...prePlayChanges({
      previousPlay: lastPlay,
      activePlay: endOfQuarterPlay,
      game: state.game
    })
  };

  return addNewPlayBase(state, play);
};

const saveGame = (state, action) => {
  const { data } = action.payload;
  const { gameDateString } = data;
  const { gameStatus } = state;

  return {
    ...state,
    shouldSave: true,
    gameStatus: transformGameStatusIfDateAdded(gameStatus, gameDateString),
    data
  };
};

const editPlay = (state, action) => {
  const play = action.payload;
  const playIndex = state.plays.findIndex((p) => p.id === play.id);
  const earlierStatePlay = _.get(state, ["plays", playIndex]);
  const previousPlay = getPreviousPlay(state.plays, playIndex);
  const transformedPlay = transformPlay(play, previousPlay, state.game);
  const pointsDiff = getPointsDiff(transformedPlay, earlierStatePlay);
  const transformedPlays = transformPlays(
    transformedPlay,
    playIndex,
    pointsDiff,
    state.plays
  );
  const transformedStats = postStatsChanges({
    plays: transformedPlays,
    state
  });

  const gameStatus = getGameStatus(transformedPlay, state.gameStatus);

  return {
    ...state,
    plays: transformedPlays,
    shouldSave: true,
    stats: transformedStats,
    gameStatus: gameStatus
  };
};

const deletePlay = (state, action) => {
  const play = action.payload;
  const playIndex = state.plays.findIndex((p) => p.id === play.id);
  const pointsDiff = getPointsDiff(null, play);
  const playsWithoutDeletedPlay = transformPlays(
    null,
    playIndex,
    pointsDiff,
    state.plays
  );
  const transformedStats = postStatsChanges({
    plays: playsWithoutDeletedPlay,
    state
  });

  return {
    ...state,
    plays: playsWithoutDeletedPlay,
    shouldSave: true,
    stats: transformedStats
  };
};

const editTeamStats = (state, action) => {
  const { away, home } = action.payload;

  const stats = {
    ..._.get(state, "stats"),
    home: {
      ..._.get(state, "stats.home"),
      teamStats: home
    },
    away: {
      ..._.get(state, "stats.away"),
      teamStats: away
    }
  };

  return {
    ...state,
    stats: stats
  };
};

const editPlayerStats = (state, action) => {
  const { side, playerStats } = action.payload;

  const stats = {
    ..._.get(state, "stats"),
    [side]: {
      ..._.get(state, ["stats", side]),
      playerStats
    }
  };

  return {
    ...state,
    stats: stats
  };
};

const calculatePlaysAndStats = (state, action) => {
  const calculatedPlays = calculatePlays(state.plays, state.game);
  const transformedStats = postStatsChanges({
    plays: calculatedPlays,
    state,
    forceOverride: true
  });

  return {
    ...state,
    shouldSave: true,
    plays: calculatedPlays,
    stats: transformedStats
  };
};

const clearGame = (state, action) => {
  const sideData = (side) => {
    return {
      playerStats: setNullPlayerStats({ stats: state.stats, side }),
      teamStats: setNullTeamStats({ stats: state.stats, side })
    };
  };

  return {
    ...state,
    shouldSave: true,
    plays: [],
    gameStatus: null,
    stats: {
      home: sideData("home"),
      away: sideData("away")
    }
  };
};

const clearNonPointsStats = (state, action) => {
  const exceptions = _.get(action, "payload.exceptions");
  const teamExceptions = _.get(exceptions, "team");
  const playerExceptions = _.get(exceptions, "player");

  const sideData = (side) => {
    return {
      playerStats: setNullPlayerStats({
        stats: state.stats,
        side,
        blockedKeys: playerExceptions
      }),
      teamStats: setNullTeamStats({
        stats: state.stats,
        side,
        exceptions: teamExceptions
      })
    };
  };

  return {
    ...state,
    shouldSave: true,
    stats: {
      home: sideData("home"),
      away: sideData("away")
    }
  };
};

const calculateStatsFromPlays = (state, action) => {
  const limitedStats = _.get(action, "payload.limitedStats");
  const shouldClearOldStats = _.get(
    action,
    "payload.shouldClearOldStats",
    true
  );
  const transformedStats = postStatsChanges({
    plays: state.plays,
    state,
    forceOverride: true,
    limitedStats,
    shouldClearOldStats
  });

  return {
    ...state,
    shouldSave: true,
    stats: transformedStats
  };
};

const playByPlayReducer = (state, action) => {
  switch (action.type) {
    case "setState":
      return setState(state, action);

    case "addNewPlay":
      return addNewPlay(state, action);

    case "endQuarter":
      return endQuarter(state, action);

    case "editPlay":
      return editPlay(state, action);

    case "deletePlay":
      return deletePlay(state, action);

    case "editTeamStats":
      return editTeamStats(state, action);

    case "editPlayerStats":
      return editPlayerStats(state, action);

    case "clearGame":
      return clearGame(state, action);

    case "clearNonPointsStats":
      return clearNonPointsStats(state, action);

    case "calculateStatsFromPlays":
      return calculateStatsFromPlays(state, action);

    case "calculatePlaysAndStats":
      return calculatePlaysAndStats(state, action);

    case "saveGame":
      return saveGame(state, action);

    default:
      return null;
  }
};

export {
  playByPlayReducer,
  saveGame,
  setState,
  addNewPlay,
  endQuarter,
  editPlay
};
